/* eslint-disable no-unused-vars */
import { FC, useState } from 'react';
import {
    Menu,
    Modal,
    notification,
    Spin,
    Typography,
} from 'antd';
import {
    FaRegTrashAlt,
    FaEdit,
    FaPaperPlane,
    FaExchangeAlt,
} from 'react-icons/fa';
import type { MenuProps } from 'antd';
import { BsLightbulbFill } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IDriver } from '../../interfaces/driver.interface';
import { deleteDriver, resendInvitation, updateDriver } from '../../api/drivers.api';
import { useWorkspaceStore } from '../../store/workspace.store';
import { checkDriverStatus } from '../../utils/driver.util';
import { useDriversContext } from './context/drivers.ctx';

type ItemType = Required<MenuProps>['items'][number];

interface MenuDriverProps {
    driver: IDriver;
}

const { confirm } = Modal;
const { Text } = Typography;

const MenuDriver: FC<MenuDriverProps> = ({ driver }) => {
    const { t: td } = useTranslation('drivers', { keyPrefix: 'DRIVERS' });
    const { t: ta } = useTranslation('shared', { keyPrefix: 'ACTIONS' });

    const workspace = useWorkspaceStore((state) => state.data);

    const { handleRemoveDriver, handleSetDriver } = useDriversContext();

    const [loading, setLoading] = useState<boolean>(false);

    const navigate = useNavigate();

    const copyDriverInvitation = () => {
        setLoading(true);
        resendInvitation(driver._id)
            .then(({ data }: any) => {
                notification.success({
                    message: td('COPY_INVITATION'),
                    description: td('COPY_INVITATION_SUCCESS'),
                });
                navigator.clipboard.writeText(data);
            })
            .catch((error: any) => {
                const isAlreadyActivated = error?.response?.status === 409
                    && error?.response?.data?.error === 'PASSWORD_RESET_TOKEN_NOT_FOUND';
                const description = isAlreadyActivated
                    ? td('INVITATION_ALREADY_ACTIVATED')
                    : error?.response?.data?.message ?? error?.response?.data?.error;
                notification.error({
                    message: td('COPY_INVITATION_ERROR'),
                    description,
                });
            })
            .finally(() => setLoading(false));
    };

    const handleDeleteDriver = () => {
        confirm({
            width: 500,
            title: td('DELETE_QUESTION'),
            content: `${driver.name} ${driver.lastName}`,
            okText: ta('DELETE'),
            cancelText: ta('CANCEL'),
            onOk() {
                setLoading(true);

                const payload: { keyName: string } = {
                    keyName: workspace?.keyName || '',
                };

                deleteDriver(driver._id, payload)
                    .then(() => {
                        notification.success({
                            message: td('DRIVER_REMOVED'),
                            description: td('DRIVER_REMOVED_SUCCESS'),
                        });
                        handleRemoveDriver(driver._id);
                    }).catch((error: any) => {
                        const description = error.response.data.message;
                        notification.error({
                            message: 'Error',
                            description,
                        });
                    }).finally(() => setLoading(false));
            },
        });
    };

    const handleChangeStatus = () => {
        const status = checkDriverStatus(driver.isBusy, driver.onDutyFlag);
        const driverName = `${driver.name} ${driver.lastName}`;

        const newStatus = status !== 'ONLINE';

        confirm({
            title: td('CHANGE_STATUS_QUESTION_DESCRIPTION', { driverName }),
            width: 600,
            content: (
                status === 'ONLINE' && (
                    <div className="help">
                        <div className="help__tag">
                            <BsLightbulbFill />
                            {td('IMPORTANT')}
                        </div>
                        <Text>
                            {td('OFF_DUTY_CHANGE_STATUS_DESCRIPTION')}
                        </Text>
                    </div>
                )
            ),
            icon: <FaExchangeAlt size={16} color="var(--color-purple)" />,
            okText: td('CHANGE_STATUS'),
            okType: 'primary',
            onOk() {
                setLoading(true);
                updateDriver(driver._id, { onDutyFlag: newStatus })
                    .then(() => {
                        notification.success({
                            message: td('CHANGE_STATUS'),
                            description: td('CHANGE_STATUS_SUCCESS'),
                        });
                        handleSetDriver(driver._id, { onDutyFlag: newStatus });
                    })
                    .catch((error: any) => {
                        const description = error.response.data.message;
                        notification.error({
                            message: 'Error',
                            description,
                        });
                    })
                    .finally(() => setLoading(false));
            },
            cancelText: ta('CANCEL'),
        });
    };

    const items: ItemType[] = [
        {
            key: 'EDIT',
            label: ta('EDIT'),
            icon: loading ? <Spin size="small" /> : <FaEdit />,
            onClick: () => navigate(`/drivers/${driver._id}`),
        },
        {
            key: 'CHANGE_STATUS',
            label: td('CHANGE_STATUS'),
            icon: loading ? <Spin size="small" /> : <FaExchangeAlt />,
            onClick: () => handleChangeStatus(),
        },
        {
            key: 'COPY_INVITATION',
            label: td('COPY_INVITATION'),
            icon: loading ? <Spin size="small" /> : <FaPaperPlane />,
            onClick: () => copyDriverInvitation(),
        },
        {
            key: 'DELETE',
            label: ta('DELETE'),
            icon: loading ? <Spin size="small" /> : <FaRegTrashAlt />,
            danger: true,
            onClick: () => handleDeleteDriver(),
        },
    ];
    return (
        <Menu items={items} />
    );
};

export { MenuDriver };
